import { memo, useCallback, useEffect } from 'react';
import { useAppendFormTableRow } from './useAppendFormTableRow';
import { ButtonProps } from 'antd';
import { useFocusTableRowField } from './useFocusTableRowField';
import { listenCreateFormTableRow } from '../../events';
import { useFormTableName } from './TanstackFormTable';
import { CreateTableRowButton } from './CreateTableRowButton';
import { useAdditionalFormContext } from '../../AdditionalFormContext';

export interface CreateFormTableRowButtonProps<
  RowData extends Record<string, any>,
> extends Omit<ButtonProps, 'id'> {
  id?: string;
  defaultRowValues?: (() => RowData) | RowData;
  autoFocusField?: Extract<keyof RowData, string>;
  onAppend?: (row: unknown) => void;
}

export const CreateFormTableRowButton = (memo as <T>(c: T) => T)(
  <RowData extends Record<string, any>>(
    props: CreateFormTableRowButtonProps<RowData>,
  ) => {
    const { defaultRowValues, autoFocusField, onAppend, ...rest } = props;
    const { onChange: formOnChange } = useAdditionalFormContext();

    const name = useFormTableName();
    const focusField = useFocusTableRowField(autoFocusField);
    const appendRow = useAppendFormTableRow();
    const appendDefaultRowAndFocus = useCallback(() => {
      formOnChange?.();
      const row =
        defaultRowValues instanceof Function
          ? { isNew: true, ...defaultRowValues() }
          : { isNew: true, ...defaultRowValues };
      appendRow(row);
      onAppend?.(row);
      focusField();
    }, [appendRow, defaultRowValues, focusField, formOnChange, onAppend]);

    useEffect(
      () =>
        listenCreateFormTableRow((event) => {
          event.detail.tableName === name && appendDefaultRowAndFocus();
        }),
      [appendDefaultRowAndFocus, name],
    );

    return (
      <CreateTableRowButton onClick={appendDefaultRowAndFocus} {...rest} />
    );
  },
);
